import { delay } from "../client.js";

const STORAGE_KEY = "console-design.account-asset-types.v3";

export const ASSET_COLUMN_TYPES = ["string", "int", "file"];

const SEED_ASSET_TYPES = {
  "acc-1": [
    {
      id: "asset-type-1",
      name: "2W",
      version: 1,
      description: "Two-wheeler asset schema with registration and insurance fields.",
      appMeta: [
        { key: "manufacturer", type: "string" },
        { key: "model", type: "string" },
        { key: "variant", type: "string" },
      ],
      assetMeta: [
        { key: "registration", type: "string" },
        { key: "model_year", type: "int" },
        { key: "insurance_doc", type: "file" },
      ],
      createdAt: "2025-01-10T08:00:00.000Z",
      updatedAt: "2025-03-15T10:30:00.000Z",
    },
    {
      id: "asset-type-2",
      name: "3W/4W",
      version: 1,
      description: "Three- and four-wheeler schema.",
      appMeta: [
        { key: "manufacturer", type: "string" },
        { key: "model", type: "string" },
        { key: "variant", type: "string" },
      ],
      assetMeta: [
        { key: "vin", type: "string" },
        { key: "odometer", type: "int" },
      ],
      createdAt: "2025-02-01T09:00:00.000Z",
      updatedAt: "2025-02-01T09:00:00.000Z",
    },
  ],
};

function cloneColumns(columns) {
  return (columns ?? []).map((c) => ({ ...c }));
}

function cloneRow(row) {
  return {
    ...row,
    appMeta: cloneColumns(row.appMeta),
    assetMeta: cloneColumns(row.assetMeta ?? row.columns),
  };
}

function cloneSeed() {
  return Object.fromEntries(
    Object.entries(SEED_ASSET_TYPES).map(([id, rows]) => [id, rows.map(cloneRow)]),
  );
}

function migrateRow(row) {
  if (!row) return row;
  const appMeta = cloneColumns(row.appMeta);
  const assetMeta = cloneColumns(row.assetMeta ?? row.columns);
  return {
    ...row,
    version: row.version ?? 1,
    description: row.description ?? "",
    appMeta,
    assetMeta,
  };
}

function loadStore() {
  const merged = cloneSeed();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return merged;
    const parsed = JSON.parse(raw);
    for (const [accountId, rows] of Object.entries(parsed)) {
      if (Array.isArray(rows)) merged[accountId] = rows.map(migrateRow);
    }
  } catch {
    /* ignore */
  }
  return merged;
}

function saveStore(store) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function nextId() {
  return `asset-type-${Date.now().toString(36)}`;
}

function getAccountAssetTypes(store, accountId) {
  if (!store[accountId]) {
    store[accountId] = SEED_ASSET_TYPES[accountId]
      ? SEED_ASSET_TYPES[accountId].map(cloneRow)
      : [];
  }
  return store[accountId];
}

function normalizeColumns(columns) {
  if (!Array.isArray(columns)) return [];
  return columns
    .map((col) => ({ key: String(col.key ?? "").trim(), type: col.type }))
    .filter((col) => col.key && ASSET_COLUMN_TYPES.includes(col.type));
}

function validatePayload(payload) {
  const name = payload.name?.trim();
  if (!name) throw new Error("Asset type name is required");

  const version = Number(payload.version);
  if (!Number.isInteger(version) || version < 1) {
    throw new Error("Version must be a whole number of 1 or greater");
  }

  const description = payload.description?.trim() ?? "";
  const appMeta = normalizeColumns(payload.appMeta);
  const assetMeta = normalizeColumns(payload.assetMeta);
  const keys = new Set();

  for (const col of [...appMeta, ...assetMeta]) {
    const lower = col.key.toLowerCase();
    if (keys.has(lower)) throw new Error(`Duplicate column key: ${col.key}`);
    keys.add(lower);
  }

  return { name, version, description, appMeta, assetMeta };
}

function toResponse(row) {
  const migrated = migrateRow(row);
  return {
    ...migrated,
    appMeta: cloneColumns(migrated.appMeta),
    assetMeta: cloneColumns(migrated.assetMeta),
  };
}

export async function listAccountAssetTypes(accountId) {
  await delay(120);
  const store = loadStore();
  return getAccountAssetTypes(store, accountId).map(toResponse);
}

export async function createAccountAssetType(accountId, payload) {
  await delay(140);
  const { name, version, description, appMeta, assetMeta } = validatePayload(payload);
  const store = loadStore();
  const rows = getAccountAssetTypes(store, accountId);
  if (rows.some((r) => r.name.toLowerCase() === name.toLowerCase())) {
    throw new Error("An asset type with this name already exists");
  }
  const now = new Date().toISOString();
  const row = {
    id: nextId(),
    name,
    version,
    description,
    appMeta,
    assetMeta,
    createdAt: now,
    updatedAt: now,
  };
  rows.push(row);
  saveStore(store);
  return toResponse(row);
}

export async function updateAccountAssetType(accountId, assetTypeId, payload) {
  await delay(120);
  const { name, version, description, appMeta, assetMeta } = validatePayload(payload);
  const store = loadStore();
  const rows = getAccountAssetTypes(store, accountId);
  const row = rows.find((r) => r.id === assetTypeId);
  if (!row) throw new Error("Asset type not found");
  if (rows.some((r) => r.id !== assetTypeId && r.name.toLowerCase() === name.toLowerCase())) {
    throw new Error("An asset type with this name already exists");
  }
  row.name = name;
  row.version = version;
  row.description = description;
  row.appMeta = appMeta.map((c) => ({ ...c }));
  row.assetMeta = assetMeta.map((c) => ({ ...c }));
  row.updatedAt = new Date().toISOString();
  saveStore(store);
  return toResponse(row);
}

export async function deleteAccountAssetType(accountId, assetTypeId) {
  await delay(100);
  const store = loadStore();
  const rows = getAccountAssetTypes(store, accountId);
  const index = rows.findIndex((r) => r.id === assetTypeId);
  if (index < 0) throw new Error("Asset type not found");
  rows.splice(index, 1);
  saveStore(store);
}
